import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useTeamMeetingTeamsQuery } from '../../../../queries/TeamMeetingConfig';
import { useLoadAllUsers } from '../../../../queries/User/useLoadAllUsers';
import { Checkbox } from '../../../components-v2/Checkbox';
import {
	Dialog,
	DialogBody,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../components-v2/Dialog';
import { Input } from '../../../components-v2/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/Select';
import { baseStyleSheet } from '../../../styles/styles';
import { BulkSettingsOverride } from './BulkSettingsOverride';
import { styleSheet } from './styles';
import { IMemberFormData, UserMeetingLinkSelector } from './UserMeetingLinkSelector';

interface ITeamMeetingConfigModalProps {
	isOpen: boolean;
	onClose: () => void;
	config: Api.ITeamMeetingConfig | null;
	onSave: (
		request: Api.ITeamMeetingConfigCreateRequest | Api.ITeamMeetingConfigUpdateRequest,
		configId?: string
	) => Promise<void>;
}

export function TeamMeetingConfigModal({ isOpen, onClose, config, onSave }: ITeamMeetingConfigModalProps) {
	const [name, setName] = React.useState('');
	const [team, setTeam] = React.useState<string>('Default');
	const [members, setMembers] = React.useState<IMemberFormData[]>([]);
	const [isSaving, setIsSaving] = React.useState(false);

	// Selection state for bulk settings
	const [selectedMemberIds, setSelectedMemberIds] = React.useState<Set<string>>(new Set());
	const [isSettingsPanelOpen, setIsSettingsPanelOpen] = React.useState(false);

	const membersEndRef = React.useRef<HTMLDivElement>(null);

	const { users, isLoading: isLoadingUsers } = useLoadAllUsers({ pageSize: 500 });
	const { data: teamTypes = [] } = useTeamMeetingTeamsQuery();

	// Only show team dropdown if there are multiple team types (not just "Default")
	const shouldShowTeamDropdown = teamTypes.length > 1 || (teamTypes.length === 1 && teamTypes[0] !== 'Default');
	const defaultTeamType = teamTypes[0] ?? 'Default';

	// Active users sorted by name
	const activeUsers = React.useMemo(() => {
		return users
			.filter(user => user.activationStatus === 'ACTIVE')
			.sort((a, b) => {
				const nameA = `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim();
				const nameB = `${b.firstName ?? ''} ${b.lastName ?? ''}`.trim();
				return nameA.localeCompare(nameB);
			});
	}, [users]);

	// Selection derived values
	const selectableMemberIds = React.useMemo(
		() => members.filter(m => m.meetingConfigId).map(m => m.meetingConfigId),
		[members]
	);

	const isAllSelected = selectableMemberIds.length > 0 && selectedMemberIds.size === selectableMemberIds.length;
	const isIndeterminate = selectedMemberIds.size > 0 && selectedMemberIds.size < selectableMemberIds.length;

	// Reset form when modal opens/closes or config changes
	React.useEffect(() => {
		if (isOpen) {
			// Reset selection state
			setSelectedMemberIds(new Set());
			setIsSettingsPanelOpen(false);

			if (config) {
				// Editing existing config
				setName(config.name);
				setTeam(config.team);
				setMembers(
					config.meetingConfigs.map((m, idx) => ({
						id: m.id,
						tempId: `existing-${idx}-${Date.now()}`,
						userId: m.userId,
						meetingConfigId: m.id,
						priority: m.priority,
						enabled: m.enabled,
					}))
				);
			} else {
				// Creating new config
				setName('');
				setTeam(defaultTeamType);
				setMembers([]);
			}
		}
	}, [isOpen, config, defaultTeamType]);

	const handleAddMember = () => {
		setMembers(prev => [
			...prev,
			{
				id: '',
				tempId: `new-${Date.now()}`,
				userId: '',
				meetingConfigId: '',
				priority: 1,
				enabled: true,
			},
		]);
		// Scroll to the new member after state update
		setTimeout(() => {
			membersEndRef.current?.scrollIntoView({ behavior: 'smooth' });
		}, 0);
	};

	const handleUpdateMember = (tempId: string, updates: Partial<IMemberFormData>) => {
		const member = members.find(m => m.tempId === tempId);
		// Clear selection if meeting config is being changed
		if (member && updates.meetingConfigId !== undefined && member.meetingConfigId !== updates.meetingConfigId) {
			setSelectedMemberIds(prev => {
				const newSet = new Set(prev);
				newSet.delete(member.meetingConfigId);
				return newSet;
			});
		}
		setMembers(prev => prev.map(m => (m.tempId === tempId ? { ...m, ...updates } : m)));
	};

	const handleRemoveMember = (tempId: string) => {
		const member = members.find(m => m.tempId === tempId);
		if (member?.meetingConfigId) {
			// Also remove from selection
			setSelectedMemberIds(prev => {
				const newSet = new Set(prev);
				newSet.delete(member.meetingConfigId);
				return newSet;
			});
		}
		setMembers(prev => prev.filter(m => m.tempId !== tempId));
	};

	// Selection handlers
	const handleSelectAll = (checked: boolean | 'indeterminate') => {
		if (checked === true) {
			setSelectedMemberIds(new Set(selectableMemberIds));
		} else {
			setSelectedMemberIds(new Set());
		}
	};

	const handleSelectionChange = (meetingConfigId: string, selected: boolean) => {
		setSelectedMemberIds(prev => {
			const newSet = new Set(prev);
			if (selected) {
				newSet.add(meetingConfigId);
			} else {
				newSet.delete(meetingConfigId);
			}
			return newSet;
		});
	};

	const handleBulkApplySuccess = () => {
		setIsSettingsPanelOpen(false);
		setSelectedMemberIds(new Set());
	};

	const validate = (): string[] => {
		const validationErrors: string[] = [];
		if (!name.trim()) {
			validationErrors.push('Name is required');
		}
		if (shouldShowTeamDropdown && !team) {
			validationErrors.push('Team type is required');
		}
		if (members.length === 0) {
			validationErrors.push('At least one team member is required');
		}
		const incompleteMember = members.find(m => !m.userId || !m.meetingConfigId);
		if (incompleteMember) {
			validationErrors.push('All members must have a user and meeting link selected');
		}
		return validationErrors;
	};

	const handleSave = async () => {
		const validationErrors = validate();
		if (validationErrors.length > 0) {
			validationErrors.forEach(error => toast.error(error));
			return;
		}

		setIsSaving(true);

		try {
			const meetingConfigs = members.map(m => ({
				id: m.meetingConfigId,
				userId: m.userId,
				priority: m.priority,
				enabled: m.enabled,
			}));

			if (config) {
				// Update
				const request: Api.ITeamMeetingConfigUpdateRequest = {
					name: name.trim(),
					team,
					enabled: config.enabled,
					meetingConfigs,
					categories: config.categories,
				};
				await onSave(request, config.id);
			} else {
				// Create
				const request: Api.ITeamMeetingConfigCreateRequest = {
					name: name.trim(),
					team,
					meetingConfigs,
				};
				await onSave(request);
			}
		} finally {
			setIsSaving(false);
		}
	};

	const isEditing = !!config;

	return (
		<Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
			<DialogPopup styles={[styleSheet.modalContent]}>
				<DialogHeader>
					<DialogTitle>{isEditing ? 'Edit Team Meeting Link' : 'Create Team Meeting Link'}</DialogTitle>
				</DialogHeader>

				<DialogBody styles={[styleSheet.dialogBody]}>
					<div className={css(styleSheet.formSection)}>
						<label className={css(styleSheet.label)} htmlFor='team-meeting-name'>
							Name
						</label>
						<Input
							id='team-meeting-name'
							type='text'
							styles={[styleSheet.input]}
							value={name}
							onChange={e => setName(e.target.value)}
							placeholder='e.g., Sales Demo Team'
						/>
					</div>

					{shouldShowTeamDropdown && (
						<div className={css(styleSheet.formSection)}>
							<label className={css(styleSheet.label)}>Team Type</label>
							<Select value={team} onValueChange={setTeam}>
								<SelectTrigger styles={styleSheet.selectTriggerFull}>
									<SelectValue placeholder='Select a team type...'>{team}</SelectValue>
								</SelectTrigger>
								<SelectContent>
									{teamTypes.map(t => (
										<SelectItem key={t} value={t}>
											{t}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					)}

					{config?.categories && config.categories.length > 0 && (
						<div className={css(styleSheet.categoriesDisplay)}>Categories: {config.categories.join(', ')}</div>
					)}

					<div className={css(styleSheet.membersSection)}>
						<div className={css(styleSheet.membersHeaderRow)}>
							<span className={css(styleSheet.membersSectionTitle)}>Team Members</span>
							{members.length > 0 && (
								<div className={css(styleSheet.selectionControls)}>
									<label className={css(styleSheet.selectAllLabel)}>
										<Checkbox
											checked={isIndeterminate ? 'indeterminate' : isAllSelected}
											onCheckedChange={handleSelectAll}
											disabled={selectableMemberIds.length === 0}
										/>
										<span>Select all</span>
									</label>
									<button
										type='button'
										className={css(
											styleSheet.editSettingsLink,
											selectedMemberIds.size === 0 && styleSheet.editSettingsLinkDisabled
										)}
										onClick={() => setIsSettingsPanelOpen(true)}
										disabled={selectedMemberIds.size === 0}
									>
										Edit Meeting Settings
									</button>
								</div>
							)}
						</div>

						<BulkSettingsOverride
							isOpen={isSettingsPanelOpen}
							onClose={() => setIsSettingsPanelOpen(false)}
							selectedMeetingConfigIds={Array.from(selectedMemberIds)}
							onApplySuccess={handleBulkApplySuccess}
						/>

						{members.length === 0 ? (
							<div className={css(styleSheet.emptyMembers)}>
								No members added yet. Click &quot;Add Member&quot; to add team members.
							</div>
						) : (
							<div className={css(styleSheet.membersList)}>
								{members.map((member, index) => (
									<UserMeetingLinkSelector
										key={member.tempId}
										member={member}
										users={activeUsers}
										index={index}
										onUpdate={handleUpdateMember}
										onRemove={handleRemoveMember}
										isSelected={selectedMemberIds.has(member.meetingConfigId)}
										onSelectionChange={handleSelectionChange}
									/>
								))}
								<div ref={membersEndRef} />
							</div>
						)}
					</div>
				</DialogBody>

				<DialogFooter>
					<div className={css(styleSheet.footerContainer)}>
						<button
							type='button'
							className={css(styleSheet.addMemberButton)}
							onClick={handleAddMember}
							disabled={isLoadingUsers}
						>
							Add Member
						</button>
						<div className={css(styleSheet.footerButtons)}>
							<button
								type='button'
								className={css(baseStyleSheet.ctaButtonReverse)}
								onClick={onClose}
								disabled={isSaving}
							>
								Cancel
							</button>
							<button type='button' className={css(baseStyleSheet.ctaButton)} onClick={handleSave} disabled={isSaving}>
								{isSaving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create'}
							</button>
						</div>
					</div>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
}
